import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { IonIcon, IonRouterLink } from '@ionic/react'
import { FetchNextPageOptions } from '@tanstack/react-query'
import {
  checkmarkOutline,
  closeCircleOutline,
  cloudUploadOutline,
  flash,
} from 'ionicons/icons'

import { Button } from '@components/atoms/Button'
import { InfiniteScroll } from '@components/molecules/InfiniteScroll'
import { VotesLabel } from '@/components/atoms/VotesLabel'
import { MarkdownUrlHighlighter } from '@/components/molecules/MarkdownUrlHighlighter'
import { StatusBadge } from '@/components/molecules/StatusBadge'

import { useUserInfo } from '@/lib/hooks/useUserInfo'

import { ContestSections, ContestStatus, Pages, RouterDirection } from '@enums'
import { IContest, IContestUser } from '@/interfaces'

export interface IContestDetailsProps {
  contest: IContest
  isJoined: boolean
  participants: IContestUser[]
  hasNextPage: boolean
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<unknown>
  onJoinClick: (contest: IContest) => void
  onVoteClick: (contesID: string, activityId: string) => void
}

export const ContestDetails: FC<IContestDetailsProps> = ({
  contest,
  isJoined,
  onJoinClick,
  participants,
  hasNextPage,
  fetchNextPage,
  onVoteClick,
}) => {
  const { t } = useTranslation('common')
  const { data: authUser } = useUserInfo()
  const isFinished = contest.endDate < Date.now()
  const isNotStarted = (contest?.submissionDate || 0) > Date.now()
  const isSubmissionNotAccepted = ![
    ContestStatus.Submission,
    ContestStatus.SubmissionVoting,
  ].includes(contest.status)

  const participantsActivityId = participants?.find(
    (activity: IContestUser) =>
      (activity as IContestUser).user.id !== authUser?.id
  )?.user.id

  return (
    <section className="w-full">
      <article className="pt-2 px-4 mb-4">
        <div className="flex mb-4">
          <div className="mr-2 relative w-5/12 h-56">
            <IonRouterLink
              routerLink={`/contest/${contest.id}/video/${contest.video.id}?channel=${ContestSections.Creator}&page=${Pages.Contest}`}
              routerDirection={RouterDirection.Forward}
            >
              <img
                src={
                  contest.creatorVideoThumbnailSmall
                    ? contest.creatorVideoThumbnailSmall
                    : contest.creatorVideoThumbnail
                }
                className="object-cover absolute h-full left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-full rounded-md"
                alt={contest.name}
              />
              <img
                src="/play.svg"
                alt="play"
                className="absolute bottom-1 right-1"
              />
            </IonRouterLink>
          </div>
          <div className="w-7/12">
            <dl className="grid gap-2 grid-cols-1 h-max mb-2">
              <div>
                <dt className="h-4 mb-1">
                  <StatusBadge status={contest.status} />
                </dt>
                <dd className="text-xs opacity-50">
                  {t('contest.details.statusLabel')}
                </dd>
              </div>
              <div>
                <dt
                  className="font-semibold text-base text-primary -mb-1 text-ellipsis overflow-hidden"
                  data-test="cdp-contest-category"
                >
                  {`#${contest.category}`}
                </dt>
                <dd className="text-xs opacity-50">
                  {t('contest.details.genre')}
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-base -mb-1 text-ellipsis overflow-hidden">
                  {t('contest.details.seconds', {
                    seconds: contest.videoLengthLimit,
                  })}
                </dt>
                <dd className="text-xs opacity-50">
                  {t('contest.details.length')}
                </dd>
              </div>
            </dl>
            <MarkdownUrlHighlighter
              className="pb-2 text-xs"
              text={contest.description}
            />
          </div>
        </div>
        {!isFinished && (
          <div className="grid grid-cols-2 gap-4">
            <Button
              expand="block"
              size="large"
              hasFullWidth={false}
              onClick={() => onJoinClick(contest)}
              type={
                isJoined
                  ? 'secondary'
                  : isFinished || isNotStarted || isSubmissionNotAccepted
                  ? 'secondary'
                  : 'primary'
              }
            >
              <IonIcon
                className="w-5 m-0 mr-1"
                icon={
                  isJoined
                    ? checkmarkOutline
                    : isFinished || isNotStarted || isSubmissionNotAccepted
                    ? closeCircleOutline
                    : cloudUploadOutline
                }
                slot="start"
              />
              {isJoined
                ? t('contest.details.joined')
                : t('contest.details.join')}
            </Button>
            <Button
              expand="block"
              size="large"
              hasFullWidth={false}
              type="primary"
              fill="outline"
              onClick={() => onVoteClick(contest.id, participantsActivityId)}
              isDisabled={
                !participantsActivityId ||
                contest.status === ContestStatus.Judging ||
                contest.status === ContestStatus.Submission
              }
            >
              <IonIcon className="w-5" icon={flash} slot="start" />
              {t('contest.details.vote')}
            </Button>
          </div>
        )}
      </article>
      <h3 className="mb-4 text-base px-4">
        <span className="opacity-50">{t('contest.details.submissions')}</span>
        <b> ({contest.participantsCount ?? 0})</b>
      </h3>
      <div className="grid gap-[0.0625rem] grid-cols-3 mb-4">
        {participants.map((participant: IContestUser) => {
          return (
            <IonRouterLink
              className="relative"
              key={participant.user?.id}
              routerLink={`/contest/${contest.id}/video/${participant?.video?.id}?channel=${ContestSections.Participant}&page=${Pages.Contest}`}
            >
              <img
                src={participant.video?.thumbnailSmall}
                alt={participant.user?.id}
                className="cursor-pointer w-full"
              />
              <div className="absolute right-1 bottom-1">
                <VotesLabel small count={participant?.votesCount} />
              </div>
            </IonRouterLink>
          )
        })}
      </div>
      <InfiniteScroll disabled={!hasNextPage} onRequest={fetchNextPage} />
    </section>
  )
}
